import {
  type Balances,
  type ChainMetadata,
  flattenBalances,
  groupByChain,
  summarizeByKind,
} from '@polaris/core/models'
import {
  cleanupAddressEventSource,
  getStreamBalances,
} from '@polaris/core/services'
import { hasSignificantBalanceChanges } from '@polaris/core/utils'
import { isServer } from '@tanstack/react-query'
import { atom } from 'jotai'
import { atomEffect } from 'jotai-effect'
import { atomWithQuery, queryClientAtom } from 'jotai-tanstack-query'
import { balancesStreamQuery } from '@/src/queries/keys/balances'
import { mpcWalletAddressesWithNamadaOverrideAtom } from '@/src/stores/mpc/atoms'
import { accountsAddressesAtom, ignoredAddressesAtom } from '../accounts/atoms'
import { walletKeyringAtom } from '../wallets/atoms'

const allAddressesAtom = atom((get) => {
  const ignoredAddresses = get(ignoredAddressesAtom)
  const accountsAddresses = get(accountsAddressesAtom)
  const walletKeyring = get(walletKeyringAtom)
  const { data: mpcAddressesMap } = get(
    mpcWalletAddressesWithNamadaOverrideAtom,
  )

  const addressSet = new Set<string>()
  for (const address of accountsAddresses) {
    addressSet.add(address)
  }

  for (const address of Object.values(walletKeyring.addresses)) {
    addressSet.add(address)
  }

  for (const address of Object.values(mpcAddressesMap ?? {})) {
    addressSet.add(address)
  }

  for (const address of ignoredAddresses) {
    addressSet.delete(address)
  }

  return Array.from(addressSet).filter(Boolean)
})

const allBalancesQueriesAtom = atom((get) => {
  const addresses = get(allAddressesAtom)

  return addresses.map((address) =>
    atomWithQuery(() => balancesStreamQuery(address)),
  )
})

const allBalancesAtom = atom((get) => {
  const addressBalances = get(allBalancesQueriesAtom)

  const balances = addressBalances.flatMap((balanceByChainTypeAtom) => {
    const balanceByChainType = get(balanceByChainTypeAtom)

    return balanceByChainType.data ?? []
  })

  return balances
})
const flattenedBalancesAtom = atom((get) =>
  flattenBalances(...get(allBalancesAtom)),
)

export const balancesByChainsAtom = atom((get) =>
  groupByChain(...get(allBalancesAtom)),
)

export const balancesSummaryAtom = atom((get) =>
  summarizeByKind(get(flattenedBalancesAtom)),
)

// Re-export from accounts.ts for backward compatibility

export const balancesStreamAtom = atomEffect((get) => {
  const addresses = get(allAddressesAtom)
  const queryClient = get(queryClientAtom)
  const { data: mpcAddressesMap } = get(
    mpcWalletAddressesWithNamadaOverrideAtom,
  )

  if (addresses.length === 0 || isServer) return

  const evtSources: EventSource[] = []
  const trackedAddresses = new Set<string>()

  // Deduplicate addresses - MPC addresses might already be in addresses array
  const allUniqueAddresses = new Set([
    ...addresses,
    ...Object.values(mpcAddressesMap ?? {}),
  ])

  for (const address of allUniqueAddresses) {
    // Skip if already tracking this address (prevents duplicate streams)
    if (trackedAddresses.has(address)) continue
    trackedAddresses.add(address)

    const evtSource = getStreamBalances({ address })

    evtSource.addEventListener('balance_update', (event) => {
      type BalanceStreamRawResponse = {
        [chainId: string]: {
          chain_metadata: ChainMetadata
          balances: {
            asset_key: {
              chain_id: string
              denom: string
              display_identifier: string
            }
            denom: string
            kind: string
            kind_logo_img_url: string
            kind_display_name: string
            display_name: string
            parent_kind_display_name: string
            precision: number
            contract_address: string
            logo_img_url: string
            price_usd?: string
            ticker: string
            normalized_amount: number
          }[]
        }
      }
      type ChainBalancesRecord = {
        /**
         * ex: cosmos:agoric-3
         */
        [chainId: string]: Balances
      }
      const data = JSON.parse(event.data) as BalanceStreamRawResponse

      // process raw response into FE types
      const newBalances: Balances[] = Object.values(data).map(
        ({ chain_metadata, balances }) => ({
          chain_metadata,
          address,
          balances: balances.map((b) => ({
            ...b,
            ...chain_metadata,
            address,
            parent_kind: b.parent_kind_display_name,
            value_usd:
              Number.parseFloat(b.price_usd ?? '0') * b.normalized_amount,
          })),
        }),
      )

      /**
       * CRITICAL FIX: Perform significance check INSIDE the updater function
       * to eliminate TOCTOU race condition.
       *
       * This ensures the check and update are atomic - we check and merge
       * using the same snapshot of data, preventing concurrent events from
       * corrupting the cache.
       */
      queryClient.setQueryData(
        balancesStreamQuery(address).queryKey,
        (currentBalances) => {
          // Convert undefined to empty array for comparison
          const oldBalances = Array.isArray(currentBalances)
            ? currentBalances
            : []

          // Check significance using the SAME data we're about to merge with
          if (!hasSignificantBalanceChanges(oldBalances, newBalances)) {
            // No significant changes, return existing reference unchanged
            // This prevents unnecessary re-renders while maintaining data integrity
            return currentBalances
          }

          // Significant changes detected, merge the balances
          const mergedBalances: ChainBalancesRecord = {}

          for (const balance of oldBalances.concat(newBalances)) {
            mergedBalances[balance.chain_metadata.chain_id] = balance
          }

          return Object.values(mergedBalances) as Balances[]
        },
      )
    })

    evtSources.push(evtSource)
  }

  // Enhanced cleanup: Close streams AND remove from cache
  return () => {
    for (const address of trackedAddresses) {
      cleanupAddressEventSource(address)
    }
  }
})
